import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { APP_API } from "@/hooks/useAppApi";
import { API } from "@/lib/api";
import { fijarCsrf, olvidarCsrf, refrescarCsrf, tokenCsrf } from "@/lib/csrf";
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Login único (Fase 1): estado del intento, en segundo plano, de iniciar
  // sesión también contra el backend de la app con las mismas credenciales.
  // Valores: idle | pending | ok | invalid_credentials | not_admin |
  // rate_limited | network_error | unknown_error.
  const [appAuthStatus, setAppAuthStatus] = useState("idle");

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(res => {
        setUser(res.data);
        // Sesión válida recuperada de la cookie, sin pasar por login: puede no
        // haber token CSRF guardado (sesión anterior a esta protección, otro
        // dispositivo, datos del sitio limpiados). Sin él, el primer guardado
        // del panel se iría en un 403. Ver `@/lib/csrf`.
        if (!tokenCsrf()) refrescarCsrf();
      })
      .catch(() => setUser(false))
      .finally(() => setLoading(false));
  }, []);

  // Sin await desde login(): el backend de la app corre en Render (posibles
  // cold-starts largos) y no debe retrasar el acceso al panel web, que hoy es
  // instantáneo. Nunca deja escapar una excepción hacia el llamador.
  const attemptAppLogin = async (email, password) => {
    setAppAuthStatus("pending");
    try {
      const res = await axios.post(
        `${APP_API}/auth/login`,
        { email, password },
        { withCredentials: false, timeout: 15000 }
      );
      const { access_token, role, name } = res.data || {};
      if (!access_token) {
        setAppAuthStatus("unknown_error");
        return;
      }
      if (role && role !== "admin") {
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_user");
        setAppAuthStatus("not_admin");
        return;
      }
      localStorage.setItem("admin_token", access_token);
      localStorage.setItem("admin_user", JSON.stringify({ email, name, role }));
      setAppAuthStatus("ok");
    } catch (err) {
      localStorage.removeItem("admin_token");
      localStorage.removeItem("admin_user");
      if (!err.response) setAppAuthStatus("network_error");
      else if (err.response.status === 401) setAppAuthStatus("invalid_credentials");
      else if (err.response.status === 429) setAppAuthStatus("rate_limited");
      else setAppAuthStatus("unknown_error");
    }
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    // El token CSRF viene en el cuerpo del login, no de la cookie: el panel y
    // el backend están en hosts distintos y `document.cookie` no ve la cookie
    // del otro host. Hay que guardarlo antes de que salga cualquier petición
    // del panel, o esa primera iría sin cabecera y el backend la rechazaría
    // con 403. Ver `@/lib/csrf`.
    if (res.data?.csrf_token) fijarCsrf(res.data.csrf_token);
    else await refrescarCsrf();
    setUser(res.data);
    attemptAppLogin(email, password);
    return res.data;
  };

  const retryAppLogin = (email, password) => attemptAppLogin(email, password);

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    olvidarCsrf();
    setUser(false);
    // Logout único: un solo login implica un solo logout.
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    setAppAuthStatus("idle");
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, appAuthStatus, retryAppLogin }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
